import Co2Period = App.Enums.Co2Period;

export const actionNeededDefaultPeriod = 'last-30-days' satisfies Co2Period;
export const actionNeededReportingPeriod = 'current-period' satisfies Co2Period;

export const dataCompliancePeriods = [
	'current-period',
	'last-month',
	'last-30-days',
	'last-90-days',
	'last-180-days',
	'last-365-days',
] as const satisfies readonly Co2Period[];

export type DataCompliancePeriod = (typeof dataCompliancePeriods)[number];

export const isDataCompliancePeriod = (period: string | null): period is DataCompliancePeriod =>
	period !== null && (dataCompliancePeriods as readonly string[]).includes(period);

export const dataCompliancePeriodLabels: Record<Co2Period, string> = {
	'current-period': 'Current reporting period',
	'last-12-months': 'Last 12 months',
	'last-6-months': 'Last 6 months',
	'last-3-months': 'Last 3 months',
	'last-365-days': 'Last 365 days',
	'last-180-days': 'Last 180 days',
	'last-90-days': 'Last 90 days',
	'last-30-days': 'Last 30 days',
	'last-month': 'Last month',
};

type CurrentEmissionPeriod = Pick<App.Data.OrganizationEmissionTargetData, 'periodStartDay' | 'periodStartMonth'>;

const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
	month: 'short',
	day: 'numeric',
	timeZone: 'UTC',
});
const monthDayYearFormatter = new Intl.DateTimeFormat('en-US', {
	month: 'short',
	day: 'numeric',
	year: 'numeric',
	timeZone: 'UTC',
});

const getTodayUtc = () => {
	const now = new Date();

	return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

const formatDateRangeLabel = (start: Date, end: Date) => {
	if (start.getUTCFullYear() === end.getUTCFullYear()) {
		return `${monthDayFormatter.format(start)} - ${monthDayYearFormatter.format(end)}`;
	}

	return `${monthDayYearFormatter.format(start)} - ${monthDayYearFormatter.format(end)}`;
};

const getCurrentPeriodDateRangeLabel = (periodStartDay: number, periodStartMonth: number) => {
	const today = getTodayUtc();
	const currentYear = today.getUTCFullYear();
	const periodStartThisYear = new Date(Date.UTC(currentYear, periodStartMonth - 1, periodStartDay));
	const startYear = today < periodStartThisYear ? currentYear - 1 : currentYear;
	const start = new Date(Date.UTC(startYear, periodStartMonth - 1, periodStartDay));

	return formatDateRangeLabel(start, today);
};

const getLastDaysDateRangeLabel = (days: number) => {
	const today = getTodayUtc();
	const start = new Date(today);
	start.setUTCDate(start.getUTCDate() - (days - 1));

	return formatDateRangeLabel(start, today);
};

const getLastCalendarMonthDateRangeLabel = () => {
	const today = getTodayUtc();
	const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
	const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));

	return formatDateRangeLabel(start, end);
};

const getPeriodDateRangeLabel = (
	period: DataCompliancePeriod,
	currentEmissionTarget?: CurrentEmissionPeriod | null,
) => {
	if (period === 'current-period') {
		return getCurrentPeriodDateRangeLabel(
			currentEmissionTarget?.periodStartDay ?? 1,
			currentEmissionTarget?.periodStartMonth ?? 1,
		);
	}

	if (period === 'last-month') return getLastCalendarMonthDateRangeLabel();
	if (period === 'last-30-days') return getLastDaysDateRangeLabel(30);
	if (period === 'last-90-days') return getLastDaysDateRangeLabel(90);
	if (period === 'last-180-days') return getLastDaysDateRangeLabel(180);
	if (period === 'last-365-days') return getLastDaysDateRangeLabel(365);

	return undefined;
};

export const getDataCompliancePeriodOptions = (currentEmissionTarget?: CurrentEmissionPeriod | null) =>
	dataCompliancePeriods.map(period => {
		const selectedDescription = getPeriodDateRangeLabel(period, currentEmissionTarget);

		return {
			value: period,
			label: dataCompliancePeriodLabels[period],
			...(selectedDescription && { description: selectedDescription, selectedDescription }),
		};
	});
